/**
 * LegNodeStatsPanel — the standard clickable Pass / Failed counter + lead
 * drill-down + outbound API Log popup, packaged as a drop-in component.
 *
 * Why this exists separately
 * --------------------------
 * The webhook / custom_api legs get this UI inline inside LegNodeConfigModal.
 * The google_sheets (and other dedicated-modal) legs render their own config
 * modal and so never saw those badges. Rather than fork the 900-line
 * LegNodeConfigModal, this component reproduces just the stats header + the
 * drill-down (leads list + per-lead API Log) so ANY leg-node modal can show it
 * with one line: <LegNodeStatsPanel dataBodyId=… nodeId=… brainFields=… />.
 *
 * Data source: identical to the webhook modal — getLegNodeStats (lifetime +
 * today counts from leg_nodes.pass_count/fail_count + hourly rollups),
 * getLegNodeLeads (recent pass/fail leads from leads.processing_log), and
 * getLegNodeDispatchLog (the exact request/response from leg_dispatch_logs).
 * All three already return correct data for a google_sheets node because the
 * leg writes those same tables via DispatchManager.recordBufferedOutcome.
 *
 * The drill-down list and the API Log open as portaled overlays ABOVE the host
 * modal, so this component doesn't need to restructure the modal it lives in.
 */

import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import * as api from '../../services/api';
import { Lead, BrainField } from '../../types';

interface Props {
  dataBodyId: string;
  nodeId: string;
  brainFields?: BrainField[];   // columns shown in the drill-down table
}

function formatReceived(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

// Most recent fail/error message recorded for THIS leg node on a lead.
function findFailReason(lead: Lead, legNodeId: string): string {
  const log = lead.processingLog;
  if (!Array.isArray(log)) {return '';}
  for (let i = log.length - 1; i >= 0; i--) {
    const e = log[i];
    if (e.legNodeId === legNodeId && (e.status === 'fail' || e.status === 'error')) {
      return e.message || `(no message; status=${e.status})`;
    }
  }
  return '';
}

const LegNodeStatsPanel: React.FC<Props> = ({ dataBodyId, nodeId, brainFields = [] }) => {
  const [stats, setStats] = useState<{ pass: number; fail: number; today?: { pass: number; fail: number } } | null>(null);
  const [statsLoading, setStatsLoading] = useState(true);

  const [leadsFilter, setLeadsFilter] = useState<'pass' | 'fail' | null>(null);
  const [leads, setLeads] = useState<Lead[]>([]);
  const [loadingLeads, setLoadingLeads] = useState(false);

  const [apiLogLead, setApiLogLead] = useState<Lead | null>(null);
  const [apiLog, setApiLog] = useState<api.DispatchLog | null>(null);
  const [apiLogLoading, setApiLogLoading] = useState(false);
  const [apiLogFull, setApiLogFull] = useState(false);

  useEffect(() => {
    if (!dataBodyId || !nodeId) {return;}
    setStatsLoading(true);
    api.getLegNodeStats(dataBodyId, nodeId)
      .then(s => { setStats(s); setStatsLoading(false); })
      .catch(() => { setStats({ pass: 0, fail: 0 }); setStatsLoading(false); });
  }, [dataBodyId, nodeId]);

  async function openLeads(filter: 'pass' | 'fail') {
    setLeadsFilter(filter);
    setLoadingLeads(true);
    try {
      setLeads(await api.getLegNodeLeads(dataBodyId, nodeId, filter, 20));
    } catch {
      setLeads([]);
    } finally {
      setLoadingLeads(false);
    }
  }

  async function openApiLog(lead: Lead) {
    setApiLogLead(lead);
    setApiLog(null);
    setApiLogFull(false);
    setApiLogLoading(true);
    try {
      setApiLog(await api.getLegNodeDispatchLog(dataBodyId, nodeId, lead.id));
    } catch {
      setApiLog(null);
    } finally {
      setApiLogLoading(false);
    }
  }

  return (
    <>
      {/* Badges */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'flex-end', textAlign: 'right', whiteSpace: 'nowrap' }}>
        <div style={{ display: 'flex', gap: 12, fontSize: 11 }}>
          {statsLoading ? (
            <span style={{ color: '#9ca3af', fontSize: 11 }}>Loading stats…</span>
          ) : stats ? (
            <>
              <button
                onClick={() => openLeads('pass')}
                style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: '#059669', fontSize: 11, fontWeight: 500, textDecoration: 'underline' }}
                title="View 20 most recent leads delivered to the sheet"
              >
                Pass: {stats.pass.toLocaleString()}
              </button>
              <button
                onClick={() => openLeads('fail')}
                style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: '#dc2626', fontSize: 11, fontWeight: 500, textDecoration: 'underline' }}
                title="View 20 most recent leads that failed at this leg"
              >
                Failed: {stats.fail.toLocaleString()}
              </button>
            </>
          ) : null}
        </div>
        {!statsLoading && stats?.today ? (
          <div style={{ display: 'flex', gap: 12, fontSize: 11, color: '#6366f1' }}
               title="Pass/fail at this leg for leads received today (org-local midnight).">
            <span>Today pass: {stats.today.pass.toLocaleString()}</span>
            <span>Today failed: {stats.today.fail.toLocaleString()}</span>
          </div>
        ) : null}
      </div>

      {/* Leads drill-down overlay */}
      {leadsFilter && createPortal(
        <div
          onClick={() => setLeadsFilter(null)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(17,24,39,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10500, padding: 20 }}
        >
          <div onClick={e => e.stopPropagation()}
               style={{ width: 'min(900px, 96vw)', maxHeight: '88vh', overflowY: 'auto', background: '#fff', borderRadius: 12, padding: 20, boxShadow: '0 20px 60px rgba(0,0,0,0.25)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <h2 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>
                {leadsFilter === 'pass' ? 'Delivered to the sheet' : 'Failed at this leg'}
              </h2>
              <div style={{ fontSize: 13, color: '#6b7280' }}>Last 20 · showing {leads.length}</div>
              <button className="btn btn-secondary btn-sm" onClick={() => setLeadsFilter(null)}>Close</button>
            </div>
            <div style={{ overflow: 'auto', maxHeight: '70vh', border: '1px solid #e5e7eb', borderRadius: 8 }}>
              {loadingLeads ? (
                <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af', fontSize: 14 }}>Loading…</div>
              ) : leads.length === 0 ? (
                <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af', fontSize: 14 }}>
                  No leads have {leadsFilter === 'pass' ? 'been delivered' : 'failed at this leg'} yet.
                </div>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ position: 'sticky', top: 0, background: '#f8f9fb', zIndex: 1, boxShadow: 'inset 0 -1px 0 #e5e7eb' }}>
                      <th style={thStyle}>Received</th>
                      <th style={thStyle}>API Log</th>
                      {leadsFilter === 'fail' && <th style={thStyle}>Reason</th>}
                      {brainFields.map(f => <th key={f.id} style={thStyle}>{f.fieldLabel}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {leads.map((lead, i) => {
                      const reason = leadsFilter === 'fail' ? findFailReason(lead, nodeId) : '';
                      return (
                        <tr key={lead.id} style={{ borderBottom: '1px solid #f3f4f6', background: i % 2 === 0 ? '#fff' : '#fafbfc' }}>
                          <td style={{ padding: '10px 12px', whiteSpace: 'nowrap', color: '#6b7280' }}>{formatReceived(lead.receivedAt)}</td>
                          <td style={{ padding: '10px 12px', whiteSpace: 'nowrap' }}>
                            {lead.dispatchLog ? (
                              <button
                                onClick={() => openApiLog(lead)}
                                title="View the exact request we sent and the response the destination returned"
                                style={{ cursor: 'pointer', border: '1px solid #d1d5db', background: '#fff', borderRadius: 6, padding: '2px 8px', fontSize: 11, fontWeight: 600, color: lead.dispatchLog.category === 'delivered' ? '#047857' : '#b91c1c' }}
                              >
                                API{lead.dispatchLog.statusCode != null ? ` · ${lead.dispatchLog.statusCode}` : ''}
                              </button>
                            ) : (
                              <span style={{ color: '#d1d5db' }}>—</span>
                            )}
                          </td>
                          {leadsFilter === 'fail' && (
                            <td title={reason} style={{ padding: '10px 12px', color: '#b91c1c', maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {reason || <span style={{ color: '#d1d5db' }}>—</span>}
                            </td>
                          )}
                          {brainFields.map(f => {
                            const v = lead.enrichedData?.[f.fieldName] ?? lead.data?.[f.fieldName];
                            const display = Array.isArray(v) ? v.join(', ') : v;
                            return (
                              <td key={f.id} style={{ padding: '10px 12px', color: '#111827', maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {display != null && display !== '' ? String(display) : <span style={{ color: '#d1d5db' }}>—</span>}
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Outbound API Log popup */}
      {apiLogLead && createPortal(
        <div
          onClick={(e) => { e.stopPropagation(); setApiLogLead(null); }}
          style={{ position: 'fixed', inset: 0, background: 'rgba(17,24,39,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 11000, padding: 20 }}
        >
          <div onClick={(e) => e.stopPropagation()}
               style={{ width: 'min(720px, 96vw)', maxHeight: '88vh', overflowY: 'auto', background: '#fff', borderRadius: 12, padding: 20, boxShadow: '0 20px 60px rgba(0,0,0,0.25)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 4 }}>
              <div>
                <h2 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>API Log</h2>
                <div style={{ fontSize: 13, color: '#6b7280', marginTop: 2 }}>
                  What we sent to Google Sheets &amp; what came back · {formatReceived(apiLogLead.receivedAt)}
                </div>
              </div>
              <button className="btn btn-secondary btn-sm" onClick={() => setApiLogLead(null)}>Close</button>
            </div>

            {apiLogLoading ? (
              <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af', fontSize: 14 }}>Loading…</div>
            ) : !apiLog ? (
              <div style={{ padding: 24, textAlign: 'center', color: '#9ca3af', fontSize: 14 }}>No API log recorded for this lead at this leg.</div>
            ) : (
              <>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', margin: '12px 0' }}>
                  <span style={{ fontSize: 12, fontWeight: 700, padding: '3px 10px', borderRadius: 999, color: apiLog.category === 'delivered' ? '#065f46' : '#991b1b', background: apiLog.category === 'delivered' ? '#d1fae5' : '#fee2e2' }}>
                    {apiLog.statusCode != null ? `HTTP ${apiLog.statusCode}` : 'No response'} · {apiLog.category}
                  </span>
                  {apiLog.latencyMs != null && <span style={{ fontSize: 12, color: '#6b7280' }}>Responded in {apiLog.latencyMs} ms</span>}
                  {apiLog.wasRetried && <span style={{ fontSize: 12, color: '#92400e', background: '#fef3c7', padding: '3px 8px', borderRadius: 999 }}>retried</span>}
                  {apiLog.bodyFormat && <span style={{ fontSize: 12, color: '#6b7280' }}>· {apiLog.bodyFormat}</span>}
                </div>

                <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4 }}>Request (the row we appended)</div>
                <div style={{ background: '#f8f9fb', border: '1px solid #e5e7eb', borderRadius: 8, padding: 12, marginBottom: 16 }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: '#9ca3af', marginBottom: 2 }}>Sheet</div>
                  <div style={{ fontFamily: 'monospace', fontSize: 12, wordBreak: 'break-all', color: '#111827', marginBottom: 10 }}>
                    {apiLog.requestUrl || <span style={{ color: '#d1d5db' }}>—</span>}
                  </div>
                  {apiLog.requestBody ? (
                    <>
                      <div style={{ fontSize: 11, fontWeight: 600, color: '#9ca3af', marginBottom: 2 }}>Row</div>
                      <pre style={{ fontFamily: 'monospace', fontSize: 12, whiteSpace: 'pre-wrap', wordBreak: 'break-all', color: '#111827', margin: 0 }}>{apiLog.requestBody}</pre>
                    </>
                  ) : null}
                </div>

                <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4 }}>Response</div>
                <div style={{ background: '#f8f9fb', border: '1px solid #e5e7eb', borderRadius: 8, padding: 12 }}>
                  <div style={{ fontSize: 12, color: '#374151', marginBottom: 8 }}>{apiLog.message}</div>
                  {(() => {
                    const body = apiLog.responseBody || '';
                    const CAP = 300;
                    const long = body.length > CAP;
                    const shown = apiLogFull || !long ? body : body.slice(0, CAP) + '…';
                    return (
                      <>
                        <pre style={{ fontFamily: 'monospace', fontSize: 12, whiteSpace: 'pre-wrap', wordBreak: 'break-all', color: '#111827', margin: 0 }}>
                          {shown || <span style={{ color: '#d1d5db' }}>(empty response)</span>}
                        </pre>
                        {long && (
                          <button onClick={() => setApiLogFull(v => !v)} style={{ marginTop: 8, cursor: 'pointer', border: 'none', background: 'none', color: '#4f46e5', fontSize: 12, fontWeight: 600, padding: 0 }}>
                            {apiLogFull ? 'Show less' : `Show full (${body.length} chars)`}
                          </button>
                        )}
                      </>
                    );
                  })()}
                </div>
              </>
            )}
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

const thStyle: React.CSSProperties = { padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: '#6b7280', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.04em', whiteSpace: 'nowrap' };

export default LegNodeStatsPanel;
